import Analytics from '@/utils/analytics';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AdCampaignEventType } from '@/enums';
import { AdCampaignModuleType } from '@/types';
import { AvailabilityMap, ItemListOption } from './item-context-types';
import { findFirstPaidSearchSku, trackAdAnalytics } from '@/utils/ads';
import {
  ItemDetailsSheet,
  Props as ItemDetailsSheetProps,
} from '@/components/orders/item-details-sheet';
import { RenderActionsFn } from '@/components/orders/item-detail-actions';
import { TrackingProperties } from '@/utils/analytics/base-reporter';
import { UpdateQuantityFn } from './new-order-actions-context-types';
import { useAuth } from './auth-context';
import { useNewOrderActionsContext } from './new-order-actions-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useRenderInDraggableSheet } from '@/utils/item-details';
import { useToasts } from './toast-context';
import { useUserContext } from './user-context';

export type OnCloseFn = () => void;

type TrackingData = TrackingProperties & {
  searchText?: string;
  source: string;
};

export interface ContextOptions {
  availableQuantityMap?: AvailabilityMap;
  onClose?: OnCloseFn;
  renderActions?: RenderActionsFn;
  updateQuantity?: UpdateQuantityFn;
}

interface ItemDetailContextType {
  closeDetailSheet: () => void;
  selectItemListOption: (
    itemListOption: ItemListOption,
    trackingData: TrackingData,
    contextOptions?: ContextOptions,
  ) => void;
}

export const ItemDetailContext = createContext<ItemDetailContextType | null>(
  null,
);

export const useItemDetailContext = () => {
  const context = useContext(ItemDetailContext);
  if (!context) {
    throw new Error('useItemDetailContext requires a ItemDetailContext');
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
}

export const ItemDetailContextProvider: React.FC<Props> = ({ children }) => {
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const { showToast } = useToasts();
  const isOfflineModeActive = useOfflineModeActive();
  const renderInDraggableSheet = useRenderInDraggableSheet();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const userUUID = useAuth((s) => s.authenticatedUserUUID);

  const closeCallbackRef = useRef<OnCloseFn>(null);
  const [availableQuantityMap, setAvailableQuantityMap] =
    useState<AvailabilityMap>();
  const [renderActionsOverride, setRenderActionsOverride] =
    useState<RenderActionsFn>();
  const searchText = useRef<string>(undefined);
  const [selectedItemListOptionQueue, setSelectedItemListOptionQueue] =
    useState<ItemListOption[]>([]);
  const [updateQuantityOverride, setUpdateQuantityOverride] =
    useState<UpdateQuantityFn>();
  const [itemDetailsSource, setItemDetailsSource] = useState('');

  const closeItemDetails = useCallback(() => {
    setSelectedItemListOptionQueue((queue) => {
      const nextQueue = queue.slice(0, -1);

      if (nextQueue.length === 0) {
        // Clear overrides if there are no items left in the queue
        setUpdateQuantityOverride(undefined);
        setRenderActionsOverride(undefined);
        setAvailableQuantityMap(undefined);
        closeCallbackRef.current?.();
        closeCallbackRef.current = null;
      }

      return nextQueue;
    });
  }, []);

  const contextValue = useMemo<ItemDetailContextType>(
    () => ({
      closeDetailSheet: closeItemDetails,
      selectItemListOption: (
        itemListOption: ItemListOption,
        trackingData: TrackingData,
        contextOptions: ContextOptions = {},
      ) => {
        if (isOfflineModeActive) {
          return;
        }

        Analytics.track('item_detail_context_open', {
          restaurantUUID,
          supplierUUID,
          variantPackUUID: String(itemListOption.value),
          ...trackingData,
        });

        setItemDetailsSource(trackingData.source);
        const firstPaidSearchSku = findFirstPaidSearchSku(itemListOption);
        const paidSearchResult = firstPaidSearchSku?.paidSearch;
        if (paidSearchResult && userUUID) {
          trackAdAnalytics({
            adCampaignUUID: paidSearchResult.ad_campaign_id,
            adManagerUUID: paidSearchResult.ad_manager_id,
            adModule: AdCampaignModuleType.PaidSearch,
            advertiserUUID: paidSearchResult.advertiser_id,
            distributorUUID: supplierUUID,
            eventType: AdCampaignEventType.CLICK,
            eventUUID: paidSearchResult.click_event_id,
            gtin: paidSearchResult.gtin,
            keyword: paidSearchResult.matching_keyword,
            operatorUUID: restaurantUUID,
            source: trackingData.source,
            userUUID,
            variantPackUUID: paidSearchResult.variant_pack_id,
          });
        }
        setSelectedItemListOptionQueue((queue) => {
          return [...queue, itemListOption].filter((option, index, array) => {
            // Remove duplicate adjacent list options
            return (
              option.value !== array[index + 1]?.value ||
              index === array.length - 1
            );
          });
        });
        setUpdateQuantityOverride(() => contextOptions.updateQuantity);
        setRenderActionsOverride(() => contextOptions.renderActions);
        setAvailableQuantityMap(contextOptions.availableQuantityMap);
        if (contextOptions.onClose) {
          closeCallbackRef.current = contextOptions.onClose;
        }
        searchText.current = trackingData.searchText;
      },
      source: itemDetailsSource,
    }),
    [
      closeItemDetails,
      isOfflineModeActive,
      itemDetailsSource,
      restaurantUUID,
      supplierUUID,
      userUUID,
    ],
  );

  const updateQuantity =
    updateQuantityOverride ??
    ((updateQuantityData) => {
      debouncedUpdateQuantity(updateQuantityData, {
        source: 'item_detail_action_sheet',
      });
    });

  const sheetProps: Omit<ItemDetailsSheetProps, 'selectedItemListOption'> = {
    availableQuantityMap,
    onClose: closeItemDetails,
    onSelectItem: (item, source) => {
      contextValue.selectItemListOption(item, { source });
    },
    renderActionsOverride,
    showToast,
    source: itemDetailsSource,
    updateQuantity,
  };

  return (
    <ItemDetailContext.Provider value={contextValue}>
      {children}
      {renderInDraggableSheet ? (
        // Draggable sheets support rendering multiple sheets at the same time,
        // with the last sheet rendered on top
        selectedItemListOptionQueue.map((option) => (
          <ItemDetailsSheet
            key={option.value}
            {...sheetProps}
            renderInDraggableSheet
            selectedItemListOption={option}
          />
        ))
      ) : selectedItemListOptionQueue.length ? (
        // Modal sheet can only be rendered one at a time, so render the latest
        <ItemDetailsSheet
          {...sheetProps}
          selectedItemListOption={
            selectedItemListOptionQueue[selectedItemListOptionQueue.length - 1]
          }
        />
      ) : null}
    </ItemDetailContext.Provider>
  );
};
